import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';

import { CategoryTreeStateEntity } from '../../models/category/categoryTree';
import {
  CategoryName,
  DbId,
  IPrivsCatalogsTreeAction,
  IPrivsTreeAction,
  ITreeNodeInstance,
  TAffiliationSign,
  TPaneSign,
  TPrivsTreeActionCode,
  TRoleId,
  TTreeId,
} from '../../models/role-privs/RolePrivsData';
import { ClientContext } from '../../services/context/IContextService';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';
import { CategoryTree } from './dm-trees/CategoryTree';
import { IExtendedTreeNodeInstance } from './dm-trees/CategoryTreeNode';

export interface ICategoryPaneFields {
  categoryTreeId: TTreeId;
  pane: TPaneSign;
}

interface IPanesWithAffiliation extends ICategoryPaneFields {
  mainTreeId: TTreeId;
  pane: TAffiliationSign;
}

interface IPanesWithoutAffiliation extends ICategoryPaneFields {
  pane: 'all';
}

type TPanesState = IPanesWithAffiliation | IPanesWithoutAffiliation;

export interface IPrivsCategoryTreeStateEntity extends CategoryTreeStateEntity {
  treeId: TTreeId;
  pane: TPaneSign;
  isCategoryTree: true;
  mainTreeId?: TTreeId;
}

export interface IPrivsMainTreeStateEntity extends CategoryTreeStateEntity {
  treeId: TTreeId;
  pane: TAffiliationSign;
  isCategoryTree: false;
  categoryTreeId: TTreeId;
}

export type TPrivsTreeStateEntity =
  IPrivsMainTreeStateEntity | IPrivsCategoryTreeStateEntity;

export type TPrivsTreeState = Record<TTreeId, TPrivsTreeStateEntity>;

interface ICategoryTreeActionsState {
  disabled: boolean;
  actions: IPrivsTreeAction[];
}

export interface RolePrivsState {
  readonly categoryName: CategoryName;
  readonly roleId: TRoleId;
  readonly workerId: DbId;
  readonly datasetId: UniqueId;
  readonly trees: TPrivsTreeState;
  readonly treeActions: Record<TTreeId, ICategoryTreeActionsState>;
  readonly inherit: boolean;
  readonly show: boolean;
  readonly panes: TPanesState[];
}

const initialState: RolePrivsState = {
  categoryName: '',
  roleId: 0,
  workerId: 1,
  datasetId: '',
  trees: {},
  treeActions: {},
  panes: [],
  inherit: false,
  show: false,
};

const getPrivsTreeFor = (
  state: RolePrivsState,
  treeId: TTreeId
): CategoryTree => {
  const stateSlice = state.trees[treeId];
  return new CategoryTree(stateSlice as TPrivsTreeStateEntity);
};

const getSelectedOrActiveNode = (
  state: RolePrivsState,
  treeId: TTreeId,
  id?: DbId
) => {
  const activeId = state.trees[treeId]?.activeId;
  return id
    ? state.trees[treeId]?.nodes.find(node => node.id === id)
    : state.trees[treeId]?.nodes.find(node => node.id === activeId);
};

const rolePrivsSlice = createSlice({
  name: 'rolePrivs',
  initialState,
  reducers: {
    open: pendingAction<RolePrivsState, UniqueId>(),
    openSuccess: successAction<
      RolePrivsState,
      {
        categoryName: string;
        roleId: TRoleId;
        context: ClientContext;
        panes: ICategoryPaneFields[];
        dataset: UniqueId;
      }
    >((state, { payload: { categoryName, roleId, context, dataset } }) => {
      state.categoryName = categoryName;
      state.roleId = roleId;
      state.workerId = context.get('Id');
      state.datasetId = dataset;
      state.show = true;
    }),
    openFailure: failureAction(),
    close() {
      return initialState;
    },
    toggleInheritPrivs(state, { payload }: PayloadAction<boolean>) {
      state.inherit = payload;
    },
    loadCategoryTreeRoot: pendingAction<
      RolePrivsState,
      {
        categoryName: CategoryName;
        roleId: TRoleId;
        panes: ICategoryPaneFields[];
      }
    >(),
    loadCategoryTreeRootSuccess: successAction<
      RolePrivsState,
      {
        categoryName: CategoryName;
        roleId: TRoleId;
        panes: ICategoryPaneFields[];
        categoryNodes: ITreeNodeInstance[];
      }
    >((state, { payload: { panes, categoryNodes } }) => {
      for (const { categoryTreeId, pane } of panes) {
        state.trees[categoryTreeId] = {
          treeId: categoryTreeId,
          pane,
          isCategoryTree: true,
          categoryId: '',
          nodes: [],
          activeId: null,
          hierarchyRootId: null,
          checkedNodes: ImmutableSet(),
          expandedNodes: ImmutableSet(),
          pendingNodes: ImmutableSet(),
          childrenCache: ImmutableMap(),
        };
        const stateSlice = state.trees[categoryTreeId];
        new CategoryTree(stateSlice as TPrivsTreeStateEntity).setRoots(
          categoryNodes
        );
        if (pane === 'all') {
          state.panes.push({ pane, categoryTreeId });
        }
      }
    }),
    loadCategoryTreeRootFailure: failureAction(),
    loadMainTreeRoot: pendingAction<
      RolePrivsState,
      { categoryTreeId: TTreeId; pane: TAffiliationSign }
    >(),
    loadMainTreeRootSuccess: successAction<
      RolePrivsState,
      {
        categoryTreeId: TTreeId;
        treeId: TTreeId;
        pane: TAffiliationSign;
        mainTreeNodes: IExtendedTreeNodeInstance[];
      }
    >((state, { payload: { treeId, categoryTreeId, pane, mainTreeNodes } }) => {
      const categoryTree = state.trees[categoryTreeId];
      if (categoryTree.isCategoryTree) {
        categoryTree.mainTreeId = treeId;
      }
      const checkedNodeIds = mainTreeNodes.reduce<DbId[]>(
        (acc, { id, hasAccess }) => (hasAccess ? [...acc, id] : acc),
        []
      );
      state.trees[treeId] = {
        treeId,
        pane,
        isCategoryTree: false,
        categoryTreeId,
        categoryId: '',
        nodes: [],
        activeId: null,
        hierarchyRootId: null,
        checkedNodes: ImmutableSet(checkedNodeIds),
        expandedNodes: ImmutableSet(),
        pendingNodes: ImmutableSet(),
        childrenCache: ImmutableMap(),
      };
      const tree = getPrivsTreeFor(state, treeId);
      const mappedPrivsTreeNodes = mainTreeNodes.map(node => ({
        ...node,
        hasAccess: true,
        withCheckbox: pane === 'subdivision' ? false : undefined,
      }));
      tree.setRoots(mappedPrivsTreeNodes);
      if (!state.panes.some((e: TPanesState) => e.pane === pane)) {
        state.panes.push({ mainTreeId: treeId, categoryTreeId, pane });
        state.panes.sort((a: TPanesState, b: TPanesState) =>
          a.pane.localeCompare(b.pane)
        );
      }
    }),
    loadMainTreeRootFailure: failureAction(),
    loadTreeNodeChildren: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; nodeId: DbId }
    >(),
    loadTreeNodeChildrenSuccess: successAction<
      RolePrivsState,
      { treeId: TTreeId; nodeId: DbId; nodes: IExtendedTreeNodeInstance[] }
    >((state, { payload: { treeId, nodes, nodeId } }) => {
      const tree = getPrivsTreeFor(state, treeId);
      const mappedPrivsTreeNodes = nodes.map(node => {
        if (node.hasAccess) {
          tree.setNodeChecked(node.id);
        }
        return { ...node, hasAccess: true };
      });

      tree.setChildren(nodeId, mappedPrivsTreeNodes);
    }),
    loadTreeNodeChildrenFailure: failureAction(),
    selectTreeNode(
      state,
      {
        payload: { treeId, nodeId },
      }: PayloadAction<{ treeId: TTreeId; nodeId: DbId }>
    ) {
      getPrivsTreeFor(state, treeId).activateNode(nodeId);
    },
    nextTreeNode(
      state,
      { payload: { treeId } }: PayloadAction<{ treeId: TTreeId }>
    ) {
      getPrivsTreeFor(state, treeId).activateNext();
    },
    prevTreeNode(
      state,
      { payload: { treeId } }: PayloadAction<{ treeId: TTreeId }>
    ) {
      getPrivsTreeFor(state, treeId).activatePrevious();
    },
    expandTreeNode(
      state,
      {
        payload: { treeId, nodeId },
      }: PayloadAction<{ treeId: TTreeId; nodeId?: DbId }>
    ) {
      const tree = getPrivsTreeFor(state, treeId);
      if (nodeId !== undefined) {
        tree.expandNode(nodeId);
      } else {
        tree.expandActiveNode();
      }
    },
    collapseTreeNode(
      state,
      {
        payload: { treeId, nodeId },
      }: PayloadAction<{ treeId: TTreeId; nodeId?: DbId }>
    ) {
      const tree = getPrivsTreeFor(state, treeId);
      if (nodeId !== undefined) {
        tree.collapseNode(nodeId);
      } else {
        tree.collapseActiveNode();
      }
    },
    markTreeNodePending(
      state,
      {
        payload: { treeId, nodeId },
      }: PayloadAction<{ treeId: TTreeId; nodeId: DbId }>
    ) {
      getPrivsTreeFor(state, treeId).markPending(nodeId);
    },
    unmarkTreeNodePending(
      state,
      {
        payload: { treeId, nodeId },
      }: PayloadAction<{ treeId: TTreeId; nodeId: DbId }>
    ) {
      getPrivsTreeFor(state, treeId).unmarkPending(nodeId);
    },
    loadCategoryTreeActions: pendingAction<
      RolePrivsState,
      { categoryTreeId: TTreeId; categoryName: CategoryName }
    >((state, { payload: { categoryTreeId } }) => {
      state.treeActions[categoryTreeId] = {
        ...state.treeActions[categoryTreeId],
        disabled: true,
      };
    }),
    loadCategoryTreeActionsSuccess: successAction<
      RolePrivsState,
      { categoryTreeId: TTreeId; categoryActionsData: IPrivsTreeAction[] }
    >((state, { payload: { categoryTreeId, categoryActionsData } }) => {
      state.treeActions[categoryTreeId].disabled = false;
      state.treeActions[categoryTreeId].actions = categoryActionsData;
    }),
    loadCategoryTreeActionsFailure: failureAction(),
    loadMainTreeActions: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; nodeId: DbId }
    >((state, { payload: { treeId } }) => {
      state.treeActions[treeId] = {
        ...state.treeActions[treeId],
        disabled: true,
      };
    }),
    loadMainTreeActionsSuccess: successAction<
      RolePrivsState,
      {
        treeId: TTreeId;
        catalogActionsData: IPrivsCatalogsTreeAction[];
        nodeId: DbId;
      }
    >((state, { payload: { treeId, catalogActionsData } }) => {
      state.treeActions[treeId].disabled = false;
      state.treeActions[treeId].actions = catalogActionsData;
    }),
    loadMainTreeActionsFailure: failureAction(),
    toggleMainTreeNodeCheck: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; pane: TAffiliationSign; nodeId?: DbId }
    >(),
    toggleMainTreeNodeCheckSuccess: successAction<
      RolePrivsState,
      { treeId: TTreeId; nodeId: DbId; checked: boolean } | undefined
    >((state, { payload }) => {
      if (!payload) {
        return;
      }
      const { treeId, nodeId, checked } = payload;
      const tree = getPrivsTreeFor(state, treeId);
      const toggleChildrenRecursive = (currentTreeId: TTreeId, id?: DbId) => {
        const node = getSelectedOrActiveNode(state, currentTreeId, id);
        if (node?.hasChildren) {
          node.children.forEach(childId => {
            checked
              ? tree.setNodeChecked(childId)
              : tree.unsetNodeChecked(childId);
            toggleChildrenRecursive(treeId, childId);
          });
        }
      };

      const toggledId = getSelectedOrActiveNode(state, treeId, nodeId)?.id;
      if (toggledId) tree.toggleNodeChecks(toggledId, [toggledId]);

      if (state.inherit) {
        toggleChildrenRecursive(treeId, nodeId);
      }

      const treeState = state.trees[treeId];
      if (!treeState.isCategoryTree) {
        const { activeId, categoryTreeId } = treeState;
        if (activeId === nodeId) {
          state.treeActions[categoryTreeId].disabled = !checked;
        }
      }
    }),
    toggleMainTreeNodeCheckFailure: failureAction(),
    setPrivs: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; action: IPrivsTreeAction }
    >(),
    setPrivsSuccess: successAction<
      RolePrivsState,
      {
        treeId: TTreeId;
        actionCode: TPrivsTreeActionCode;
        hasAccess: boolean;
      }
    >((state, { payload: { treeId, actionCode, hasAccess } }) => {
      const modActionIndex = state.treeActions[treeId].actions.findIndex(
        (stateAction: IPrivsTreeAction) => stateAction.actionCode === actionCode
      );
      if (modActionIndex !== -1) {
        state.treeActions[treeId].actions[modActionIndex] = {
          ...state.treeActions[treeId].actions[modActionIndex],
          hasAccess: hasAccess ? 1 : 0,
        };
      }
    }),
    setPrivsFailure: failureAction(),
    setCategoryPrivs: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; action: IPrivsTreeAction }
    >(),
    setCategoryPrivsSuccess: successAction<
      RolePrivsState,
      {
        treeId: TTreeId;
        actionCode: TPrivsTreeActionCode;
        hasAccess: boolean;
      }
    >((state, { payload: { treeId, actionCode, hasAccess } }) => {
      const modActionIndex = state.treeActions[treeId].actions.findIndex(
        (stateAction: IPrivsTreeAction) => stateAction.actionCode === actionCode
      );
      if (modActionIndex !== -1) {
        state.treeActions[treeId].actions[modActionIndex] = {
          ...state.treeActions[treeId].actions[modActionIndex],
          hasAccess: hasAccess ? 1 : 0,
        };
      }
    }),
    setCategoryPrivsFailure: failureAction(),
    setCatalogAllPrivs: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; catalogId: DbId }
    >(),
    setCatalogAllPrivsSuccess: successAction<
      RolePrivsState,
      { treeId: TTreeId; catalogId: DbId }
    >((state, { payload: { treeId } }) => {
      state.treeActions[treeId].actions = state.treeActions[treeId].actions.map(
        (action: IPrivsTreeAction) => ({ ...action, hasAccess: 1 })
      );
    }),
    setCatalogAllPrivsFailure: failureAction(),
    setCategoryAllPrivs: pendingAction<
      RolePrivsState,
      { treeId: TTreeId; mainTreeActiveId: DbId }
    >(),
    setCategoryAllPrivsSuccess: successAction<
      RolePrivsState,
      { treeId: TTreeId }
    >((state, { payload: { treeId } }) => {
      state.treeActions[treeId].actions = state.treeActions[treeId].actions.map(
        (action: IPrivsTreeAction) => ({
          ...action,
          hasAccess: 1,
        })
      );
    }),
    setCategoryAllPrivsFailure: failureAction(),
  },
});

export const rolePrivsReducer = rolePrivsSlice.reducer;

export default rolePrivsSlice.actions;
